import type { IInfo } from '@boc/organization/types';
import type { IChannel } from '@boc/saleor-common/channels/types';
import { getAttributeValue } from '../../products/attributes';
import { variantName } from '../../products/variant-helpers';
import type {
  ProductDetailsFragment,
  ProductVariantDetailsFragment,
} from '../../saleor-graphql';

import type { ProductGroupSchemaInput, ProductSchemaInput } from './types';

export interface IReview {
  name: string;
  rating: number;
  text: string;
  date?: string;
}

interface SaleorProductSchemaOptions {
  variant: ProductVariantDetailsFragment;
  product?: ProductDetailsFragment;
  channel: IChannel;
  siteUrl: string;
  productUrl: string;
  reviews?: IReview[];
  organization: IInfo;
  shippingRate?: number;
  freeShippingThreshold?: number;
  deliveryDays?: {
    min: number;
    max: number;
  };
}

/**
 * Extract text from EditorJS blocks
 */
const blocksToText = (blocks?: string | null) => {
  if (!blocks) return '';

  try {
    const blocksJson = JSON.parse(blocks) as {
      blocks: Array<{ type: string; data: { text: string } }>;
    };

    return blocksJson.blocks?.length
      ? blocksJson.blocks
          .filter((block) => block.type === 'paragraph')
          .map((block) =>
            block.data.text
              .replace(/<[^>]+>/g, '')
              .replace(/&nbsp;/g, ' ')
              .replace(/&amp;/g, '&')
              .trim()
          )
          .join('\n\n')
      : '';
  } catch {
    return '';
  }
};

/**
 * Get product images from variant and product
 */
const getProductImages = (
  variant: ProductVariantDetailsFragment,
  product?: ProductDetailsFragment
): string[] => {
  const images: string[] = [];

  // Add variant images
  if (variant.media) {
    images.push(...variant.media.map((m) => m.url));
  }

  // Add product thumbnail if not already included
  const productData = product || variant.product;

  if (productData.thumbnail?.url && !images.includes(productData.thumbnail.url)) {
    images.unshift(productData.thumbnail.url); // Put thumbnail first
  }

  // Add product media if available (media is on full product, not variant.product)
  if (product?.media) {
    product.media.forEach((m) => {
      if (m.url && !images.includes(m.url)) {
        images.push(m.url);
      }
    });
  }

  return images;
};

/**
 * Convert Saleor product data to structured data format
 */
export const convertSaleorToProductSchema = ({
  variant,
  product,
  channel,
  siteUrl,
  productUrl,
  reviews = [],
  organization,
  shippingRate = 0,
  freeShippingThreshold,
  deliveryDays,
}: SaleorProductSchemaOptions): ProductSchemaInput => {
  const productData = product || variant.product;
  const currency = variant.pricing?.price?.gross.currency || channel.defaultCurrency || 'USD';

  // Calculate shipping rate based on price and threshold
  let finalShippingRate = shippingRate;

  if (freeShippingThreshold && variant.pricing?.price?.gross.amount) {
    if (variant.pricing.price.gross.amount >= freeShippingThreshold) {
      finalShippingRate = 0;
    }
  }

  // Get brand from attributes
  const brand =
    getAttributeValue(productData.attributes, 'brand') ||
    getAttributeValue(productData.attributes, 'publisher') ||
    getAttributeValue(productData.attributes, 'manufacturer') ||
    organization.name;

  // Get identifiers
  const gtin =
    getAttributeValue(productData.attributes, 'gtin') ||
    getAttributeValue(productData.attributes, 'isbn') ||
    getAttributeValue(productData.attributes, 'ean');
  const mpn = getAttributeValue(productData.attributes, 'mpn');

  // Aggregate rating comes from real reviews only. Saleor's product.rating is not a
  // 1-5 star average here, so it must not be published as ratingValue.
  const aggregateRating =
    reviews.length > 0
      ? {
          ratingValue: reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length,
          reviewCount: reviews.length,
        }
      : undefined;

  return {
    id: variant.id,
    name: variantName({ ...variant, product: productData }),
    description:
      productData.seoDescription || blocksToText(productData.description) || productData.name,
    image: getProductImages(variant, product),
    url: productUrl,
    sku: variant.sku || variant.id,
    gtin: gtin || undefined,
    mpn: mpn || undefined,
    brand,

    // Pricing
    price: variant.pricing?.price?.gross.amount || 0,
    priceCurrency: currency,
    previousPrice: variant.pricing?.priceUndiscounted?.gross.amount,
    priceValidUntil: variant.pricing?.discount
      ? (() => {
          // Use today's midnight UTC + 30 days to avoid SSR/client hydration mismatch
          const today = new Date();
          today.setUTCHours(0, 0, 0, 0);
          return new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
        })()
      : undefined,

    // Availability - using isAvailable from product
    availability: productData.isAvailable ? 'InStock' : 'OutOfStock',

    // Condition
    condition: 'NewCondition',

    // Ratings
    aggregateRating,
    reviews: reviews.map((review) => ({
      author: review.name,
      reviewRating: review.rating,
      reviewBody: review.text,
      datePublished: review.date,
    })),

    // Dimensions - prefer variant-level weight, fall back to product-level
    weight: (() => {
      const w = variant.weight ?? product?.weight;
      if (!w) return undefined;
      return {
        value: w.value,
        unitCode:
          w.unit === 'KG'
            ? 'KGM'
            : w.unit === 'G'
              ? 'GRM'
              : w.unit === 'LB'
                ? 'LBR'
                : w.unit === 'OZ'
                  ? 'ONZ'
                  : 'KGM',
      };
    })(),

    // Shipping
    shippingDetails: {
      shippingRate: finalShippingRate,
      shippingCurrency: currency,
      shippingDestination: channel.supportedCountries || [channel.defaultCountry],
      deliveryTime: deliveryDays
        ? {
            minDays: deliveryDays.min,
            maxDays: deliveryDays.max,
          }
        : undefined,
    },

    // Seller
    seller: {
      name: organization.name,
      url: organization.url || siteUrl,
    },
  };
};

/**
 * Extract variant attributes for product group
 */
const extractVariantAttributes = (
  variant: ProductVariantDetailsFragment,
  variesBy: string[]
): Record<string, string> => {
  const attributes: Record<string, string> = {};

  // Map common attribute names to schema.org properties
  const attributeMap: Record<string, string> = {
    color: 'color',
    colour: 'color',
    size: 'size',
    material: 'material',
    pattern: 'pattern',
    gender: 'suggestedGender',
    age: 'suggestedAge',
    'age-group': 'suggestedAge',
  };

  variant.attributes.forEach((attr) => {
    if (attr.attribute.slug) {
      const schemaProperty = attributeMap[attr.attribute.slug.toLowerCase()];

      if (schemaProperty && variesBy.includes(`https://schema.org/${schemaProperty}`)) {
        const value = attr.values[0]?.name;

        if (value) {
          attributes[schemaProperty] = value;
        }
      }
    }
  });

  return attributes;
};

/**
 * Convert Saleor product with variants to ProductGroup schema
 */
export const convertSaleorToProductGroupSchema = ({
  product,
  variants,
  channel,
  siteUrl,
  baseProductUrl,
  reviews = [],
  organization,
  shippingRate = 0,
  freeShippingThreshold,
  deliveryDays,
}: {
  product: ProductDetailsFragment;
  variants: ProductVariantDetailsFragment[];
  channel: IChannel;
  siteUrl: string;
  baseProductUrl: string;
  reviews?: IReview[];
  organization: IInfo;
  shippingRate?: number;
  freeShippingThreshold?: number;
  deliveryDays?: {
    min: number;
    max: number;
  };
}): ProductGroupSchemaInput => {
  // Determine what attributes vary between variants
  const varyingAttributes = new Set<string>();
  const attributeMap: Record<string, string> = {
    color: 'https://schema.org/color',
    colour: 'https://schema.org/color',
    size: 'https://schema.org/size',
    material: 'https://schema.org/material',
    pattern: 'https://schema.org/pattern',
    gender: 'https://schema.org/suggestedGender',
    age: 'https://schema.org/suggestedAge',
    'age-group': 'https://schema.org/suggestedAge',
  };

  // Check which attributes vary
  variants.forEach((variant) => {
    variant.attributes.forEach((attr) => {
      if (attr.attribute.slug) {
        const schemaProperty = attributeMap[attr.attribute.slug.toLowerCase()];

        if (schemaProperty) {
          varyingAttributes.add(schemaProperty);
        }
      }
    });
  });

  const variesBy = Array.from(varyingAttributes) as ProductGroupSchemaInput['variesBy'];

  // Convert each variant
  const variantSchemas = variants.map((variant) => {
    const variantSchema = convertSaleorToProductSchema({
      variant,
      product,
      channel,
      siteUrl,
      productUrl: `${baseProductUrl}?variant=${variant.id}`,
      reviews: reviews.filter(
        (r) => r.text.includes(variant.name) || r.text.includes(variant.sku || '')
      ),
      organization,
      shippingRate,
      freeShippingThreshold,
      deliveryDays,
    });

    // Add variant-specific attributes
    variantSchema.variantAttributes = extractVariantAttributes(variant, variesBy);

    return variantSchema;
  });

  return {
    id: product.id,
    name: product.name,
    description: product.seoDescription || blocksToText(product.description),
    variesBy,
    variants: variantSchemas,
    brand:
      getAttributeValue(product.attributes, 'brand') ||
      getAttributeValue(product.attributes, 'publisher') ||
      getAttributeValue(product.attributes, 'manufacturer') ||
      organization.name,
  };
};
